import { getJSON, postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  PAY_RATE_CARDS_QUERY_KEY,
  USER_SETTINGS_PAYRATE_CREATE_QUERY_KEY,
  USER_SETTINGS_PAYRATE_DELETE_QUERY_KEY,
  USER_SETTINGS_PAYRATE_UPDATE_QUERY_KEY,
  USER_SETTINGS_QUERY_KEY,
  USER_SETTINGS_URL
} from './queryConstants'
import type { PaymentCard } from './usePaymentCards'

export interface UserPayrateVersion {
  payRateCardId: string
  effectiveFrom: string | null
  supersededAt: string | null
  payRateCard?: PaymentCard
}

export interface UserPayrateResponse {
  userId: string
  payRateCardId: string
  effectiveFrom: string | null
  createdAt: string
  updatedAt: string
  supersededAt: string | null
  versions: UserPayrateVersion[]
  payRateCard?: PaymentCard
  previousVersion: UserPayrateVersion | null
}

export interface CreateUserPayrateDto {
  payRateCardInitialId: string
}

export interface UpdateUserPayrateDto extends CreateUserPayrateDto {
  effectiveFrom: string
}

function getPayrateUpdateErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'object' && error !== null) {
    const e = error as { msg?: string | string[]; message?: string }
    if (Array.isArray(e.msg) && e.msg.length) {
      return e.msg.filter(Boolean).join(', ')
    }
    if (typeof e.msg === 'string') return e.msg
    if (typeof e.message === 'string') return e.message
  }
  return 'Something went wrong. Please try again.'
}

export const useGetUserSettings = (userId?: string) => {
  return useQuery<UserPayrateResponse | null>(
    [USER_SETTINGS_QUERY_KEY, userId],
    async () => {
      try {
        const response = await getJSON({
          url: `${USER_SETTINGS_URL}/${userId}/payrate`,
          isRota: true
        })
        return response?.payRateCardId ? response : null
      } catch {
        return null
      }
    },
    { enabled: !!userId }
  )
}

export const useCreateUserPayrate = (userId?: string) => {
  const queryClient = useQueryClient()

  const { mutateAsync: createPayrate, isLoading: isCreating } = useMutation<
    UserPayrateResponse,
    unknown,
    CreateUserPayrateDto
  >(
    [USER_SETTINGS_PAYRATE_CREATE_QUERY_KEY, userId],
    async (dto) => {
      const response = await postJSON({
        url: `${USER_SETTINGS_URL}/${userId}/payrate`,
        json: dto,
        isRota: true
      })
      return response as UserPayrateResponse
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([USER_SETTINGS_QUERY_KEY, userId])
        queryClient.invalidateQueries([PAY_RATE_CARDS_QUERY_KEY])
      },
      onError: (error: unknown) => {
        showToast({
          type: 'error',
          title: getPayrateUpdateErrorMessage(error)
        })
      }
    }
  )

  return { createPayrate, isCreating }
}

export const useUpdateUserPayrate = (userId?: string) => {
  const queryClient = useQueryClient()

  const { mutateAsync: updatePayrate, isLoading: isUpdating } = useMutation<
    UserPayrateResponse,
    unknown,
    UpdateUserPayrateDto
  >(
    [USER_SETTINGS_PAYRATE_UPDATE_QUERY_KEY, userId],
    async (dto) => {
      const response = await postJSON({
        url: `${USER_SETTINGS_URL}/${userId}/payrate`,
        method: 'PUT',
        json: dto,
        isRota: true
      })
      return response as UserPayrateResponse
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([USER_SETTINGS_QUERY_KEY, userId])
        queryClient.invalidateQueries([PAY_RATE_CARDS_QUERY_KEY])
      },
      onError: (error: unknown) => {
        showToast({
          type: 'error',
          title: getPayrateUpdateErrorMessage(error)
        })
      }
    }
  )

  return { updatePayrate, isUpdating }
}

export const useDeleteUserPayrate = (userId?: string) => {
  const queryClient = useQueryClient()

  const { mutateAsync: deletePayrate, isLoading: isDeleting } = useMutation<
    void,
    unknown,
    void
  >(
    [USER_SETTINGS_PAYRATE_DELETE_QUERY_KEY, userId],
    async () => {
      await postJSON({
        url: `${USER_SETTINGS_URL}/${userId}/payrate`,
        method: 'DELETE',
        isRota: true
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([USER_SETTINGS_QUERY_KEY, userId])
        queryClient.invalidateQueries([PAY_RATE_CARDS_QUERY_KEY])
      },
      onError: (error: unknown) => {
        showToast({
          type: 'error',
          title: getPayrateUpdateErrorMessage(error)
        })
      }
    }
  )

  return { deletePayrate, isDeleting }
}
